import { Checkbox, TableBody, TableCell, TableRow } from '@client/toolkit';
import React from 'react';
import { ExpanableTableWraper, SortDirection } from '../../components/expanable-table-wraper';
import { TableExpandableCell } from '../../components/table-expanable-cell';
import { ContractExpirationDetailReportDto, TenantContractExpirationDetailsDto } from '@client/api';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { PortfolioPaths } from '../../../portfolio/portfolio.router';
import { MdOpenInNew } from 'react-icons/md';
import { AgreementsPaths } from '../../../agreements/agreements.router';

export const ExpiringContractTable = ({
  loading,
  data,
  onSelected,
  onSort,
  sortColumn,
  sortDirection,
}: {
  loading?: boolean;
  data: ContractExpirationDetailReportDto[];
  onSelected?: (ids: Set<string>) => void;
  onSort?: (col: string, direction: SortDirection) => void;
  sortColumn?: string;
  sortDirection?: SortDirection;
}) => {
  const { t, i18n } = useTranslation('reporting');
  const navigate = useNavigate();
  const columns = [
    { key: 'buildingName', label: t('contractLength.building'), isSortable: true, className: 'w-[25%]' },
    { key: 'tenantsAmount', label: t('contractLength.tenants'), className: 'w-[25%]' },
    { key: 'remainingMonths', label: t('contractLength.remainingMonths'), className: 'w-[10%]' },
    { key: 'endDate', label: t('contractLength.endDate'), className: 'w-[10%]' },
    { key: 'contactId', label: t('contractLength.contractId'), className: 'w-[25%]' },
  ];
  return (
    <ExpanableTableWraper
      loading={loading}
      onSort={onSort}
      onSelected={onSelected}
      columns={columns}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      data={data}
      renderBody={(
        data,
        selectedRows,
        expandedRows,
        handleToggleSelect,
        handleKeyDownSelect,
        handleToggleExpand,
        handleKeyDownExpand,
      ) => (
        <TableBody>
          {data.map((building) => (
            <React.Fragment key={building.buildingId}>
              <TableRow
                className="bg-surface-elevated/40 border-b border-surface-elevated group hover:bg-surface-elevated"
                onClick={() =>
                  navigate(generatePath(PortfolioPaths.BuildingAgreements, { buildingId: building.buildingId }))
                }
              >
                <TableCell className="px-2 py-2 align-top content-center">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedRows.has(building.buildingId)}
                    onChange={() => handleToggleSelect(building.buildingId)}
                    tabIndex={0}
                    onKeyDown={(e) => handleKeyDownSelect(e, building.buildingId)}
                  />
                </TableCell>
                <TableExpandableCell
                  label={building.buildingName}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(building.buildingId);
                  }}
                  onKeyDown={(e) => handleKeyDownExpand(e, building.buildingId)}
                  isExpanded={expandedRows.has(building.buildingId)}
                  icon={<MdOpenInNew className="text-brand-primary" />}
                />
                <TableCell className="px-2 py-2 align-top">
                  {t('table.numberOfTenants', { count: building.numberOfTenants ?? 0 })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
              </TableRow>
              {expandedRows.has(building.buildingId) &&
                building.tenants.map((tenant: TenantContractExpirationDetailsDto) => (
                  <React.Fragment key={`${tenant.agreementId}`}>
                    <TableRow className="bg-surface-base border-b border-surface-elevated">
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2 align-top">{tenant.tenantName}</TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {t('table.remainingMonths', { count: tenant.remainingContractExpirationMonths ?? 0 })}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {tenant.expirationDate && !isNaN(Date.parse(tenant.expirationDate))
                          ? new Date(tenant.expirationDate).toLocaleDateString(i18n.language)
                          : '-'}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        <Link
                          to={generatePath(AgreementsPaths.Agreement, { id: tenant.agreementId })}
                          className="text-brand-primary hover:underline"
                        >
                          {tenant.contractName}
                        </Link>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      )}
    />
  );
};
